import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
  skipToken,
} from '@tanstack/react-query';
import { trackOperation } from './trackOperation';

/**
 * Creates a normalized query name for Sentry tracking by removing specific IDs
 * @param queryKey The query key array
 * @returns A normalized query name for grouping similar queries
 */
function getNormalizedQueryName(queryKey: unknown[]): string {
  if (!queryKey.length) return 'empty';

  // Create a normalized version of the query key
  return queryKey
    .map((part, index) => {
      // Keep the first part (usually the resource name) as is
      if (index === 0) return String(part);

      // Check if the part is an ID-like value (number or string that looks like an ID)
      if (
        typeof part === 'number' ||
        (typeof part === 'string' && /^[0-9a-f-]+$/.test(part))
      ) {
        return '{id}';
      }

      // For objects, return a placeholder
      if (typeof part === 'object' && part !== null) {
        return '{params}';
      }

      // Otherwise, keep the part as is
      return String(part);
    })
    .join(':');
}

// Wrapper for useQuery with Sentry performance tracking
export function useTrackedQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends unknown[] = unknown[]
>(
  options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
  operationName?: string
): UseQueryResult<TData, TError> {
  // Extract queryKey from options
  const queryKey = options.queryKey;

  // Get both the full and normalized query names
  const fullQueryName = Array.isArray(queryKey)
    ? queryKey.join(':')
    : String(queryKey);
  const normalizedQueryName = getNormalizedQueryName(
    Array.isArray(queryKey) ? queryKey : [queryKey]
  );

  // Use explicit operationName if provided, otherwise use normalized queryKey
  const spanName = operationName || `query:${normalizedQueryName}`;

  // Extract service from query name (e.g., 'targets:list' -> 'targets')
  const queryNameForType = operationName || normalizedQueryName;
  const service = queryNameForType.includes(':') 
    ? queryNameForType.split(':')[0] 
    : 'unknown';

  // Extract operation action from query name (e.g., 'targets:list' -> 'list')
  const operationAction = queryNameForType.includes(':') 
    ? queryNameForType.split(':').slice(1).join(':') 
    : 'unknown';

  // Wrap the queryFn with our generic Sentry span tracker
  // Only wrap if queryFn is actually a function (not skipToken)
  const trackedQueryFn =
    options.queryFn && typeof options.queryFn === 'function'
      ? trackOperation(
          options.queryFn,
          spanName,
          'query',
          {
            query_key: fullQueryName,
            query_key_normalized: normalizedQueryName,
            operation_name: operationName || normalizedQueryName,
            service: service,
            operation_action: operationAction,
          }
        )
      : options.queryFn;

  // Use the original useQuery with our wrapped function (React Query v5 API)
  return useQuery<TQueryFnData, TError, TData, TQueryKey>({
    ...options,
    queryFn: trackedQueryFn,
  });
}
